import { useCallback, useState } from 'react';
import { cognitoConfirmForgotPassword, cognitoForgotPassword } from '../../pages/login/cognito';

const COGNITO_CLIENT_ID = process.env.REACT_APP_CLIENT_ID ?? '';
const COGNITO_REGION = 'eu-central-1';

export interface ForgotPasswordResult {
  success: boolean;
  error?: string;
}

/**
 * Platform-agnostic hook for handling forgot password flow.
 * Manages requesting password reset code and confirming new password.
 */
export interface UseForgotPasswordResult {
  isProcessing: boolean;
  requestResetCode: (email: string) => Promise<ForgotPasswordResult>;
  confirmNewPassword: (
    email: string,
    code: string,
    newPassword: string,
  ) => Promise<ForgotPasswordResult>;
}

export function useForgotPassword(): UseForgotPasswordResult {
  const [isProcessing, setIsProcessing] = useState(false);

  const requestResetCode = useCallback(async (email: string): Promise<ForgotPasswordResult> => {
    setIsProcessing(true);

    try {
      const result = await cognitoForgotPassword(COGNITO_REGION, COGNITO_CLIENT_ID, email);

      setIsProcessing(false);

      if (result.success) {
        return { success: true };
      } else {
        if (result.error === 'UserNotFound') {
          return { success: false, error: 'No account found with this email address' };
        }
        if (result.error === 'LimitExceeded') {
          return { success: false, error: 'Too many attempts. Please try again later' };
        }
        return { success: false, error: 'Failed to send reset code. Please try again' };
      }
    } catch (error) {
      setIsProcessing(false);
      return { success: false, error: 'An unexpected error occurred' };
    }
  }, []);

  const confirmNewPassword = useCallback(
    async (email: string, code: string, newPassword: string): Promise<ForgotPasswordResult> => {
      setIsProcessing(true);

      try {
        const result = await cognitoConfirmForgotPassword(
          COGNITO_REGION,
          COGNITO_CLIENT_ID,
          email,
          code,
          newPassword,
        );

        setIsProcessing(false);

        if (result.success) {
          return { success: true };
        } else {
          if (result.error === 'InvalidCode') {
            return { success: false, error: 'Invalid verification code' };
          }
          if (result.error === 'ExpiredCode') {
            return { success: false, error: 'Verification code has expired' };
          }
          if (result.error === 'InvalidPassword') {
            return { success: false, error: 'Password does not meet requirements' };
          }
          return { success: false, error: 'Failed to reset password. Please try again' };
        }
      } catch (error) {
        setIsProcessing(false);
        return { success: false, error: 'An unexpected error occurred' };
      }
    },
    [],
  );

  return {
    isProcessing,
    requestResetCode,
    confirmNewPassword,
  };
}
